import { useCallback } from "react";
import { useClipboard } from "./useClipboard";

export interface CopyPasteConfig<T, Target> {
    // Functions
    getSelectedItems: (selectedIds: string[]) => T[];
    saveItems: (items: T[]) => Promise<void>;
    deleteItems: (items: T[]) => Promise<void>;
    transformItemsForPaste: (items: T[], target: Target, metadata?: Record<string, any>) => T[];
    extractMetadata?: (items: T[]) => Record<string, any>;

    // Callbacks
    onCopy?: (items: T[]) => void;
    onCut?: (items: T[]) => void;
    onPaste?: (items: T[], target: Target) => void;
    onError?: (error: any) => void;
}

export const useCopyPasteOperations = <T, Target>(
    config: CopyPasteConfig<T, Target>
) => {
    const {
        clipboard,
        copyToClipboard,
        cutToClipboard,
        clearClipboard,
        hasItems,
        isCopy,
        isCut
    } = useClipboard<T>();

    const handleCopy = useCallback((selectedIds: string[]) => {
        if (!selectedIds.length) return;

        try {
            const items = config.getSelectedItems(selectedIds);
            const metadata = config.extractMetadata?.(items);
            copyToClipboard(items, metadata);
            config.onCopy?.(items);
        } catch (error) {
            config.onError?.(error);
        }
    }, [config, copyToClipboard]);

    const handleCut = useCallback((selectedIds: string[]) => {
        if (!selectedIds.length) return;

        try {
            const items = config.getSelectedItems(selectedIds);
            const metadata = config.extractMetadata?.(items);
            cutToClipboard(items, metadata);
            config.onCut?.(items);
        } catch (error) {
            config.onError?.(error);
        }
    }, [config, cutToClipboard]);

    const handlePaste = useCallback(async(target: Target) => {
        if (!hasItems) return;

        try {
            const transformedItems = config.transformItemsForPaste(
                clipboard.data,
                target,
                clipboard.metadata
            );

            await config.saveItems(transformedItems);

            if (isCut) {
                await config.deleteItems(clipboard.data);
                clearClipboard();
            }

            config.onPaste?.(transformedItems, target);
        } catch (error) {
            config.onError?.(error);
        }
    }, [clipboard, hasItems, isCut, config, clearClipboard]);

    return {
        clipboard,
        handleCopy,
        handleCut,
        handlePaste,
        clearClipboard,
        hasItems,
        isCopy,
        isCut
    };
};